import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { AITwinCreationStatus, useAppSelector } from '../../../hooks';
import { Permissions, ProspectCategory, ProspectPageTabs, ProspectType, Roles, UploadMediaType } from '../../../types';
import CreateAITwin from './CreateAITwin';
import CreateCustomSimulation from './CreateCustomSimulation';
import CreateFromExistingSimulation from './CreateFromExistingSimulation';
import { CreateSimulationEnum, CreateSimulationStepEnum } from './CreateSimulationModal';

interface CreateSimulationStepProps {
  selectedCreateSimulationType: CreateSimulationEnum;
  setVisitedTabs: Dispatch<SetStateAction<Set<ProspectPageTabs>>>;
  setCurrentStep: Dispatch<SetStateAction<CreateSimulationStepEnum>>;
  setMediaType: Dispatch<SetStateAction<UploadMediaType | undefined>>;
  setAiTwinCreationStatus: Dispatch<SetStateAction<AITwinCreationStatus | undefined>>;
  aiTwinCreationStatus?: AITwinCreationStatus;
  mediaType?: UploadMediaType;
  targetOrgId?: string;
}

const CreateSimulationStep = ({
  selectedCreateSimulationType,
  setVisitedTabs,
  setCurrentStep,
  setMediaType,
  setAiTwinCreationStatus,
  aiTwinCreationStatus,
  targetOrgId,
  mediaType,
}: CreateSimulationStepProps) => {
  const [newCategory, setNewCategory] = useState<ProspectCategory>();
  const [newType, setNewType] = useState<ProspectType>();

  const { user, organization } = useAppSelector((state) => state.auth);

  const permissions = user?.permissions || [];
  const isHiringModuleEnabled = !!organization?.isHiringModuleEnabled;

  const isSuperAdmin = permissions.includes(Permissions.ADMIN_ACCESS);
  const isSalesRep = user?.role === Roles.SALES_REP;

  // Filters prospect categories based on user/org permissions.
  const filterProspectCategories = useCallback(
    (category: ProspectCategory) => {
      // Super-admins can see all public categories.
      if (isSuperAdmin) return true;
      // If the user is a sales rep, only show practice category.
      if (isSalesRep) return category === ProspectCategory.PRACTICE;
      // Only orgs with the hiring module enabled can see Interview category.
      if (category === ProspectCategory.INTERVIEW) return isHiringModuleEnabled;
      // All other categories are public.
      return true;
    },
    [isSuperAdmin, isSalesRep, isHiringModuleEnabled]
  );

  // Goes back to type selection step.
  const onBack = useCallback(() => {
    setCurrentStep(CreateSimulationStepEnum.CREATE_SIMULATION_TYPE_STEP);
  }, []);

  return (
    <>
      {selectedCreateSimulationType === CreateSimulationEnum.CUSTOM && (
        <CreateCustomSimulation
          newCategory={newCategory}
          newType={newType}
          setNewCategory={setNewCategory}
          setNewType={setNewType}
          goToPrevStep={onBack}
          filterProspectCategories={filterProspectCategories}
        />
      )}
      {selectedCreateSimulationType === CreateSimulationEnum.TEMPLATE && (
        <CreateFromExistingSimulation
          setVisitedTabs={setVisitedTabs}
          targetOrgId={targetOrgId}
          filterProspectCategories={filterProspectCategories}
          goToPrevStep={onBack}
        />
      )}
      {selectedCreateSimulationType === CreateSimulationEnum.AI_TWIN && (
        <CreateAITwin
          filterProspectCategories={filterProspectCategories}
          goToPrevStep={onBack}
          setNewCategory={setNewCategory}
          setAiTwinCreationStatus={setAiTwinCreationStatus}
          aiTwinCreationStatus={aiTwinCreationStatus}
          setNewType={setNewType}
          setMediaType={setMediaType}
          newCategory={newCategory}
          newType={newType}
          mediaType={mediaType}
        />
      )}
    </>
  );
};

export default CreateSimulationStep;
